import {
  EXAMPLE_JUNK_REMOVAL_NASHVILLE,
  EXAMPLE_YEAR_DATA,
  EXAMPLE_TRAFFIC_HISTORY,
  EXAMPLE_MODAL_TRAFFIC_HISTORY,
  EXAMPLE_SORTED_BY_TOTAL,
  EXAMPLE_SORTED_BY_SENTIMENT,
  EXAMPLE_SORTED_BY_GROWTH,
  EXAMPLE_SORTED_BY_TRAFFIC,
  EXAMPLE_SORTED_BY_SEO,
  buildDemoPricing,
} from './exampleCompetitorMapData';

export {
  EXAMPLE_JUNK_REMOVAL_NASHVILLE,
  EXAMPLE_YEAR_DATA,
  EXAMPLE_TRAFFIC_HISTORY,
  EXAMPLE_MODAL_TRAFFIC_HISTORY,
  EXAMPLE_SORTED_BY_TOTAL,
  EXAMPLE_SORTED_BY_SENTIMENT,
  EXAMPLE_SORTED_BY_GROWTH,
  EXAMPLE_SORTED_BY_TRAFFIC,
  EXAMPLE_SORTED_BY_SEO,
  buildDemoPricing,
};

const businesses = EXAMPLE_JUNK_REMOVAL_NASHVILLE;

const DEMO_BUSINESS_IMAGES = {};
businesses.forEach((biz) => {
  DEMO_BUSINESS_IMAGES[biz.maps_uri] = null;
  DEMO_BUSINESS_IMAGES[biz.name] = null;
});
export { DEMO_BUSINESS_IMAGES };

export const DEMO_DATE_RANGE = {
  startDate: '2025-04',
  endDate: '2026-03',
};

const buildCombinedData = () => {
  return businesses.map((biz) => {
    const yearInfo = EXAMPLE_YEAR_DATA.find((y) => y.maps_uri === biz.maps_uri);
    return {
      ...biz,
      historicalData: yearInfo?.data || biz.historicalData || {},
    };
  });
};
export const DEMO_COMBINED_DATA = buildCombinedData();

export const DEMO_PRICING_DATA = {
  total_businesses: businesses.length,
  businesses_with_pricing: businesses.length,
  market_summary: {
    avg_price_level: 2.1,
    most_common_tier: 2,
    price_range: { min: 1, max: 3 },
    avg_rating: 4.64,
  },
  businesses: businesses.map((biz, idx) => {
    const pricing = buildDemoPricing(biz);
    return {
      name: biz.name,
      maps_uri: biz.maps_uri,
      address: biz.address,
      rating_avg: biz.rating_avg,
      rating_count: biz.rating_count,
      primary_type: biz.primary_type,
      price_level: pricing.price_level,
      price_label: pricing.price_label,
      perception_label: pricing.perception_label,
      perception_score: pricing.perception_score,
      classification: pricing.classification,
      market_rank: pricing.market_rank,
      top_positive_keywords: pricing.top_positive_keywords,
      top_negative_keywords: pricing.top_negative_keywords,
      mentions: pricing.mentions,
      value_score: Math.round(60 + (biz.rating_avg - 4.0) * 40 - (pricing.price_level - 1) * 10),
    };
  }),
};

export const DEMO_LOCALYTICS_RANKINGS = (() => {
  const rankingItems = businesses.map((biz) => {
    const visitors = biz.traffic_data?.metrics?.estimated_monthly_visitors || 500;
    const reviewScore = Math.min(100, Math.round((biz.rating_count / 650) * 40 + (biz.rating_avg / 5) * 30));
    const trafficScore = Math.min(100, Math.round(Math.log10(Math.max(visitors, 1)) * 25));
    const sentBreakdown = biz.historicalData.sentimentBreakdown;
    const sentTotal = sentBreakdown.positive + sentBreakdown.neutral + sentBreakdown.negative;
    const sentimentScore = Math.round((sentBreakdown.positive / sentTotal) * 100);
    const totalReviews = Object.values(biz.historicalData.monthlyReviewCounts).reduce((s, v) => s + v, 0);
    const growthScore = Math.min(100, Math.round(totalReviews / 2));
    const seoScore = biz.spyfu_data?.domain_stats?.results?.[0]?.strength || 40;
    const localytics_score = Math.round(
      growthScore * 0.2 + reviewScore * 0.25 + sentimentScore * 0.2 + trafficScore * 0.2 + seoScore * 0.15
    );
    const months = Object.keys(biz.historicalData.monthlyReviewCounts).sort();
    const threeMonthGrowth = months.slice(-3).reduce(
      (s, m) => s + (biz.historicalData.monthlyReviewCounts[m] || 0), 0
    );
    return {
      name: biz.name,
      business_name: biz.name,
      maps_uri: biz.maps_uri,
      address: biz.address,
      rating_avg: biz.rating_avg,
      rating_count: biz.rating_count,
      primary_type: biz.primary_type,
      localytics_score,
      overall_score: localytics_score,
      review_score: reviewScore,
      traffic_score: trafficScore,
      sentiment_score: sentimentScore,
      growth_score: growthScore,
      seo_score: seoScore,
      tier: localytics_score >= 85 ? 'Excellent' : localytics_score >= 70 ? 'Good' : localytics_score >= 55 ? 'Fair' : localytics_score >= 40 ? 'Below Average' : localytics_score >= 25 ? 'Poor' : 'Needs Improvement',
      strengths: ['Review Volume', 'Customer Satisfaction'],
      weaknesses: ['SEO Optimization', 'Ad Spend'],
      traffic_data: biz.traffic_data,
      spyfu_data: biz.spyfu_data,
      historicalData: biz.historicalData,
      trafficValue: visitors,
      threeMonthGrowth,
      sentimentScore,
    };
  })
    .sort((a, b) => b.localytics_score - a.localytics_score)
    .map((item, idx) => ({
      ...item,
      rank: idx + 1,
      rankings: { growth: idx + 1, total_reviews: idx + 1 },
    }));

  return {
    businesses: rankingItems,
    rankingResults: rankingItems,
    totalBusinesses: rankingItems.length,
    yearData: EXAMPLE_YEAR_DATA,
    sortedBySeo: [...rankingItems].sort((a, b) => (b.seo_score || 0) - (a.seo_score || 0)),
    market_averages: {
      avg_review_score: 55,
      avg_traffic_score: 45,
      avg_sentiment_score: 82,
      avg_overall_score: 60,
    },
  };
})();

export const DEMO_CONTENT_TEMPLATES = [
  {
    id: 'template-1',
    title: '5 Signs You Need Professional Junk Removal',
    content_type: 'blog_post',
    vertical: 'junk_removal',
    preview: 'Is your garage overflowing? Are you putting off that basement cleanout? Here are 5 clear signs it\'s time to call the professionals...',
    full_content: '# 5 Signs You Need Professional Junk Removal\n\n## 1. Your Garage Has Become a Storage Unit\nIf you can\'t park your car in the garage anymore, it\'s time to reclaim that space.\n\n## 2. You\'re Planning a Move\nDon\'t pay to move things you don\'t need. A pre-move cleanout saves time and money.\n\n## 3. Estate Cleanout Needed\nHandling a loved one\'s belongings is emotional. Professional junk removal makes it easier.\n\n## 4. Renovation Debris Piling Up\nConstruction waste requires proper disposal. Don\'t risk code violations.\n\n## 5. It\'s Been More Than a Year\nIf you haven\'t used it in a year, it\'s time to let it go.',
    target_audience: 'Homeowners',
    estimated_read_time: '4 min',
    seo_keywords: ['junk removal', 'decluttering', 'garage cleanup'],
    created_at: '2026-03-01T10:00:00Z',
  },
  {
    id: 'template-2',
    title: 'Junk Removal vs. Dumpster Rental: Which Is Right for You?',
    content_type: 'comparison_guide',
    vertical: 'junk_removal',
    preview: 'Deciding between hiring a junk removal service and renting a dumpster? Here\'s a comprehensive comparison to help you choose...',
    full_content: '# Junk Removal vs. Dumpster Rental\n\nBoth options have their place. Here\'s when to choose each.\n\n## Choose Junk Removal When:\n- You want someone else to do the heavy lifting\n- The job can be done in a single visit\n- You need items hauled from inside the home\n\n## Choose Dumpster Rental When:\n- You\'re doing a multi-day renovation\n- You can load the dumpster yourself\n- You need to dispose of construction debris over time',
    target_audience: 'Homeowners, Property Managers',
    estimated_read_time: '5 min',
    seo_keywords: ['junk removal vs dumpster', 'waste disposal options'],
    created_at: '2026-02-15T10:00:00Z',
  },
  {
    id: 'template-3',
    title: 'How Much Does Junk Removal Cost in Nashville?',
    content_type: 'pricing_guide',
    vertical: 'junk_removal',
    preview: 'Understanding junk removal pricing in the Nashville market. From single item pickup to full estate cleanouts...',
    full_content: '# Junk Removal Pricing Guide - Nashville, TN\n\n## Average Costs\n- Single item: $75-$150\n- Half truck load: $200-$400\n- Full truck load: $400-$800\n- Estate cleanout: $1,000-$3,000+\n\n## Factors That Affect Price\n1. Volume of junk\n2. Type of materials\n3. Accessibility\n4. Location within Nashville metro',
    target_audience: 'Cost-conscious homeowners',
    estimated_read_time: '3 min',
    seo_keywords: ['junk removal cost nashville', 'junk hauling prices'],
    created_at: '2026-02-01T10:00:00Z',
  },
  {
    id: 'template-4',
    title: 'Google Review Response Templates for Junk Removal',
    content_type: 'review_response',
    vertical: 'junk_removal',
    preview: 'Ready-to-use templates for responding to positive and negative reviews on Google...',
    full_content: '# Review Response Templates\n\n## Positive Review Response\n"Thank you so much for the kind words, [Name]! We\'re thrilled we could help with your [project type]. Our team takes pride in making the process easy and stress-free. We look forward to helping you again!"\n\n## Negative Review Response\n"We appreciate you sharing your experience, [Name]. We\'re sorry we didn\'t meet your expectations. We\'d love the chance to make this right. Please contact us at [phone] so we can discuss how to resolve this."',
    target_audience: 'Business owners',
    estimated_read_time: '3 min',
    seo_keywords: ['review response templates', 'google review replies'],
    created_at: '2026-01-20T10:00:00Z',
  },
  {
    id: 'template-5',
    title: 'Social Media Post Ideas for Junk Removal Companies',
    content_type: 'social_media',
    vertical: 'junk_removal',
    preview: '30 days of social media content ideas specifically designed for junk removal businesses...',
    full_content: '# 30 Social Media Post Ideas\n\n1. Before/after transformation photos\n2. Team member spotlight\n3. Customer testimonial video\n4. "Did you know?" recycling facts\n5. Behind-the-scenes truck loading timelapse\n6. Community cleanup volunteer day\n7. Seasonal decluttering tips\n8. FAQ answer: "What items do you take?"\n9. Price comparison infographic\n10. Same-day service availability post',
    target_audience: 'Social media managers',
    estimated_read_time: '6 min',
    seo_keywords: ['social media junk removal', 'content ideas'],
    created_at: '2026-01-10T10:00:00Z',
  },
];

export const DEMO_PLAYBOOK_ACTIONS = [
  {
    action_id: 'action-1',
    title: 'Respond to all Google Reviews from the last 30 days',
    description: 'Review response rate is below 50%. Responding to reviews improves SEO ranking and customer trust. Prioritize negative reviews first.',
    priority: 'high',
    type: 'reputation',
    status: 'pending',
    impact_score: 85,
    effort_level: 'medium',
    goal_metric: 'review_response_rate',
    goal_target: '90%',
    due_date: '2026-03-25',
    created_at: '2026-03-10T08:00:00Z',
  },
  {
    action_id: 'action-2',
    title: 'Set up automated review request SMS after each job',
    description: 'Top competitors are generating 15+ reviews per month. Implementing automated SMS requests can increase review velocity by 3x.',
    priority: 'high',
    type: 'growth',
    status: 'in_progress',
    impact_score: 92,
    effort_level: 'low',
    goal_metric: 'monthly_reviews',
    goal_target: '20+',
    due_date: '2026-03-20',
    created_at: '2026-03-05T08:00:00Z',
  },
  {
    action_id: 'action-3',
    title: 'Optimize Google Business Profile with service area pages',
    description: 'Create dedicated landing pages for each Nashville neighborhood you serve. This improves local SEO visibility for "near me" searches.',
    priority: 'medium',
    type: 'seo',
    status: 'pending',
    impact_score: 78,
    effort_level: 'high',
    goal_metric: 'organic_traffic',
    goal_target: '+30%',
    due_date: '2026-04-01',
    created_at: '2026-03-08T08:00:00Z',
  },
  {
    action_id: 'action-4',
    title: 'Launch Google Ads campaign targeting "junk removal near me"',
    description: 'Competitors are spending $2,000-$5,000/month on Google Ads. A targeted campaign can capture high-intent searchers in your service area.',
    priority: 'medium',
    type: 'advertising',
    status: 'pending',
    impact_score: 75,
    effort_level: 'medium',
    goal_metric: 'monthly_leads',
    goal_target: '50+',
    due_date: '2026-04-15',
    created_at: '2026-03-01T08:00:00Z',
  },
  {
    action_id: 'action-5',
    title: 'Add online booking to your website',
    description: 'Customers expect instant booking. Add a scheduling widget to capture leads 24/7 and reduce phone call volume by 40%.',
    priority: 'high',
    type: 'conversion',
    status: 'completed',
    impact_score: 88,
    effort_level: 'medium',
    goal_metric: 'conversion_rate',
    goal_target: '+25%',
    due_date: '2026-03-15',
    created_at: '2026-02-20T08:00:00Z',
    completed_at: '2026-03-12T14:30:00Z',
  },
  {
    action_id: 'action-6',
    title: 'Create a referral program with incentives',
    description: 'Word-of-mouth is the #1 driver for local services. Offer $25 off next service for each successful referral to accelerate organic growth.',
    priority: 'low',
    type: 'growth',
    status: 'pending',
    impact_score: 65,
    effort_level: 'low',
    goal_metric: 'referral_rate',
    goal_target: '15%',
    due_date: '2026-04-30',
    created_at: '2026-03-12T08:00:00Z',
  },
];

export const DEMO_READING_ITEMS = [
  {
    id: 'read-1',
    title: 'The Complete Guide to Local SEO for Service Businesses',
    url: 'https://example.com/local-seo-guide',
    source: 'Moz',
    category: 'SEO',
    scope: 'strategic',
    description: 'Comprehensive guide covering Google Business Profile optimization, local citations, and review strategy for service-area businesses.',
    estimated_read_time: 15,
    is_read: false,
    priority: 'high',
    added_at: '2026-03-10T08:00:00Z',
  },
  {
    id: 'read-2',
    title: 'How to Get More Google Reviews: 12 Proven Strategies',
    url: 'https://example.com/google-reviews-strategies',
    source: 'BrightLocal',
    category: 'Reputation',
    scope: 'tactical',
    description: 'Actionable strategies for increasing review velocity including email templates, SMS scripts, and QR code approaches.',
    estimated_read_time: 8,
    is_read: true,
    priority: 'high',
    added_at: '2026-03-05T08:00:00Z',
    read_at: '2026-03-08T10:00:00Z',
  },
  {
    id: 'read-3',
    title: 'Google Ads for Home Services: Budget Optimization Guide',
    url: 'https://example.com/google-ads-home-services',
    source: 'Search Engine Journal',
    category: 'Advertising',
    scope: 'tactical',
    description: 'How to set up and optimize Google Ads campaigns for home service businesses. Includes bid strategies and negative keyword lists.',
    estimated_read_time: 12,
    is_read: false,
    priority: 'medium',
    added_at: '2026-03-01T08:00:00Z',
  },
  {
    id: 'read-4',
    title: 'Customer Experience Playbook for Local Businesses',
    url: 'https://example.com/cx-playbook',
    source: 'Harvard Business Review',
    category: 'Operations',
    scope: 'strategic',
    description: 'Research-backed framework for building a customer experience that drives reviews, referrals, and repeat business.',
    estimated_read_time: 20,
    is_read: false,
    priority: 'medium',
    added_at: '2026-02-25T08:00:00Z',
  },
  {
    id: 'read-5',
    title: 'Pricing Psychology: How to Position Your Service Business',
    url: 'https://example.com/pricing-psychology',
    source: 'Inc. Magazine',
    category: 'Strategy',
    scope: 'strategic',
    description: 'How pricing perception affects customer decisions. Includes frameworks for value-based pricing in competitive markets.',
    estimated_read_time: 10,
    is_read: true,
    priority: 'low',
    added_at: '2026-02-20T08:00:00Z',
    read_at: '2026-03-01T14:00:00Z',
  },
];

export const getDemoData = (dashboardType) => {
  const base = {
    reviewsData: businesses,
    yearData: EXAMPLE_YEAR_DATA,
    combinedData: DEMO_COMBINED_DATA,
    businessImages: DEMO_BUSINESS_IMAGES,
    trafficHistory: EXAMPLE_TRAFFIC_HISTORY,
    modalTrafficHistory: EXAMPLE_MODAL_TRAFFIC_HISTORY,
    sortedByTotal: EXAMPLE_SORTED_BY_TOTAL,
    sortedBySentiment: EXAMPLE_SORTED_BY_SENTIMENT,
    sortedByGrowth: EXAMPLE_SORTED_BY_GROWTH,
    sortedByTraffic: EXAMPLE_SORTED_BY_TRAFFIC,
    sortedBySeo: EXAMPLE_SORTED_BY_SEO,
    dateRange: DEMO_DATE_RANGE,
    buildDemoPricing,
  };

  switch (dashboardType) {
    case 'pricing':
      return { ...base, pricingData: DEMO_PRICING_DATA };
    case 'localytics-score':
      return { ...base, rankingData: DEMO_LOCALYTICS_RANKINGS };
    case 'content-templates':
      return { ...base, templates: DEMO_CONTENT_TEMPLATES };
    case 'playbook-actions':
      return { ...base, actions: DEMO_PLAYBOOK_ACTIONS };
    case 'playbook-reading':
      return { ...base, readingItems: DEMO_READING_ITEMS };
    default:
      return base;
  }
};
